import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { State } from '../redux/redux.state';
import { GetBillingResponse, ShopBilling } from '../models/billing';
import client from '../clients/client';
import { CreateOrUpdateBillingRequest } from '../requests/create.or.update.billing.request';
import { Status } from '../redux/redux.status';

type BillingState = State<GetBillingResponse | undefined> & {
  createOrUpdateStatus: Status;
  resetBillingStatus: Status;
  devBillingStatus: Status;
  activateBillingStatus: Status;
};

const initialState: BillingState = {
  data: undefined,
  status: 'idle',
  createOrUpdateStatus: 'idle',
  resetBillingStatus: 'idle',
  activateBillingStatus: 'idle',
  devBillingStatus: 'idle'
};

export const getBilling = createAsyncThunk<GetBillingResponse, string>(
  'billing/getBilling',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`billings/${id}`);
      return data;
    } catch (err: any) {
      const { status, data } = err.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const createOrUpdateBilling = createAsyncThunk<
  ShopBilling,
  CreateOrUpdateBillingRequest
>(
  'billing/createOrUpdateBilling',
  async (request: CreateOrUpdateBillingRequest, { rejectWithValue }) => {
    try {
      const { shop_id, ...payload } = request;
      const { data } = await client.post(`billings/update/${shop_id}`, payload);
      return data;
    } catch (err: any) {
      const { status, data } = err.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const devBilling = createAsyncThunk<ShopBilling, string>('billing/devBilling', 
async(shop_id:string, {rejectWithValue}) => {
  try {
    const { data } = await client.post(`billings/dev/${shop_id}`,);
    return data;
  } catch (err: any) {
    const { status, data } = err.response;
    return rejectWithValue({ status, errorMessage: data });
  }
})

export const resetBilling = createAsyncThunk<ShopBilling, string>('billing/resetBilling', 
async(shop_id:string, {rejectWithValue}) => {
  try {
    const { data } = await client.put(`billings/reset/${shop_id}`,);
    return data;
  } catch (err: any) {
    const { status, data } = err.response;
    return rejectWithValue({ status, errorMessage: data });
  }
})

export const activateBilling = createAsyncThunk<ShopBilling, string>('billing/reactivateBilling', 
async(shop_id:string, {rejectWithValue}) => {
  try {
    const { data } = await client.put(`billings/reactivate/${shop_id}`,);
    return data;
  } catch (err: any) {
    const { status, data } = err.response;
    return rejectWithValue({ status, errorMessage: data });
  }
})

const actionSlice = createSlice({
  name: 'billings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBilling.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createOrUpdateBilling.pending, (state) => {
        state.createOrUpdateStatus = 'loading';
      })
      .addCase(devBilling.pending, (state) => {
        state.devBillingStatus = 'loading';
      })
      .addCase(resetBilling.pending, (state) => {
        state.resetBillingStatus = 'loading';
      })
      .addCase(activateBilling.pending, (state) => {
        state.activateBillingStatus = 'loading';
      })
      .addCase(getBilling.fulfilled, (state, action) => {
        state.status = 'successful';
        state.data = action.payload;
      })
      .addCase(createOrUpdateBilling.fulfilled, (state, action) => {
        state.createOrUpdateStatus = 'successful';
      })
      .addCase(devBilling.fulfilled, (state, action) => {
        state.devBillingStatus = 'successful';
      })
      .addCase(resetBilling.fulfilled, (state, action) => {
        state.resetBillingStatus = 'successful';
      })
      .addCase(activateBilling.fulfilled, (state, action) => {
        state.activateBillingStatus = 'successful';
      })
      .addCase(getBilling.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(createOrUpdateBilling.rejected, (state, action) => {
        state.createOrUpdateStatus = 'error';
        state.errors = action.payload;
      })
      .addCase(devBilling.rejected, (state, action) => {
        state.devBillingStatus = 'error';
        state.errors = action.payload;
      })
      .addCase(resetBilling.rejected, (state, action) => {
        state.resetBillingStatus = 'error';
        state.errors = action.payload;
      })
      .addCase(activateBilling.rejected, (state, action) => {
        state.activateBillingStatus = 'error';
        state.errors = action.payload;
      });
  },
});

export default actionSlice.reducer;
